import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';

// Configuración de ponderación para evaluaciones 180°
const PONDERACION_CONFIG = {
  autoevaluacion: 0.4,    // 40% peso de la autoevaluación
  supervisor: 0.6         // 60% peso de la evaluación del supervisor
};

/**
 * Función para calcular el promedio de calificaciones de una evaluación
 * @param {Object} respuestas - Objeto con las respuestas de la evaluación
 * @returns {number} - Promedio de las calificaciones
 */
const calcularPromedioCalificaciones = (respuestas) => {
  const calificaciones = [];
  
  for (const [key, value] of Object.entries(respuestas)) {
    if (value && typeof value.calificacion === 'number' && value.calificacion >= 1 && value.calificacion <= 5) {
      calificaciones.push(value.calificacion);
    }
  }
  
  if (calificaciones.length === 0) {
    return 0;
  }
  
  const suma = calificaciones.reduce((acc, calif) => acc + calif, 0);
  return suma / calificaciones.length;
};

/**
 * Función para obtener las evaluaciones completadas de un empleado
 * @param {string} empleadoCedula - Cédula del empleado
 * @returns {Object} - Objeto con autoevaluación y evaluación de supervisor
 */
const obtenerEvaluacionesEmpleado = async (empleadoCedula) => {
  try {
    // Buscar autoevaluación
    const autoevaluacionQuery = query(
      collection(db, 'autoevaluaciones_realizadas'),
      where('empleado_cedula', '==', empleadoCedula)
    );
    const autoevaluacionSnapshot = await getDocs(autoevaluacionQuery);
    
    // Buscar evaluación del supervisor
    const supervisorQuery = query(
      collection(db, 'evaluaciones_realizadas'),
      where('empleado_cedula', '==', empleadoCedula)
    );
    const supervisorSnapshot = await getDocs(supervisorQuery);
    
    const autoevaluacion = autoevaluacionSnapshot.empty ? null : {
      id: autoevaluacionSnapshot.docs[0].id,
      ...autoevaluacionSnapshot.docs[0].data()
    };
    
    const evaluacionSupervisor = supervisorSnapshot.empty ? null : {
      id: supervisorSnapshot.docs[0].id,
      ...supervisorSnapshot.docs[0].data()
    };
    
    return { autoevaluacion, evaluacionSupervisor };
  } catch (error) {
    console.error('Error obteniendo evaluaciones del empleado:', error);
    return { autoevaluacion: null, evaluacionSupervisor: null };
  }
};

/**
 * Función para procesar automáticamente una evaluación 180° terminada
 * @param {string} empleadoCedula - Cédula del empleado
 * @returns {boolean} - True si se procesó exitosamente
 */
const procesarEvaluacion180Automatica = async (empleadoCedula) => {
  try {
    console.log(`🚀 Procesando evaluación 180° automática para empleado: ${empleadoCedula}`);
    
    // 1. Obtener las evaluaciones del empleado
    const { autoevaluacion, evaluacionSupervisor } = await obtenerEvaluacionesEmpleado(empleadoCedula);
    
    if (!autoevaluacion || !evaluacionSupervisor) {
      console.log(`❌ Empleado ${empleadoCedula} no tiene ambas evaluaciones completadas:`);
      console.log(`   - Autoevaluación: ${autoevaluacion ? '✅' : '❌'}`);
      console.log(`   - Evaluación supervisor: ${evaluacionSupervisor ? '✅' : '❌'}`);
      return false;
    }
    
    // 2. Calcular promedios individuales
    const promedioAutoevaluacion = calcularPromedioCalificaciones(autoevaluacion.respuestas);
    const promedioSupervisor = calcularPromedioCalificaciones(evaluacionSupervisor.respuestas);
    
    console.log(`📊 Promedios calculados:`);
    console.log(`   - Autoevaluación: ${promedioAutoevaluacion.toFixed(2)}`);
    console.log(`   - Evaluación supervisor: ${promedioSupervisor.toFixed(2)}`);
    
    // 3. Calcular ponderado final
    const ponderadoFinal = (promedioAutoevaluacion * PONDERACION_CONFIG.autoevaluacion) + 
                          (promedioSupervisor * PONDERACION_CONFIG.supervisor);
    
    console.log(`🎯 Ponderado final: ${ponderadoFinal.toFixed(2)}`);
    
    // 4. Obtener información del empleado
    const empleadoQuery = query(
      collection(db, 'empleados'),
      where('cedula', '==', empleadoCedula)
    );
    const empleadoSnapshot = await getDocs(empleadoQuery);
    
    const infoEmpleado = empleadoSnapshot.empty ? {} : empleadoSnapshot.docs[0].data();
    
    // 5. Verificar si ya existe un registro para este empleado
    const existeQuery = query(
      collection(db, 'eval180_terminada'),
      where('empleado_cedula', '==', empleadoCedula)
    );
    const existeSnapshot = await getDocs(existeQuery);
    
    // 6. Preparar los datos del documento
    const documentoEval180 = {
      empleado_cedula: empleadoCedula,
      empleado_nombre: infoEmpleado?.nombre || autoevaluacion.empleado_nombre || 'No especificado',
      empleado_cargo: infoEmpleado?.cargo || 'No especificado',
      empleado_empresa: infoEmpleado?.empresa || 'No especificado',
      
      // Información de la evaluación
      plantilla_id: autoevaluacion.plantilla_id || evaluacionSupervisor.plantilla_id,
      evaluacion_nombre: autoevaluacion.evaluacion_nombre || evaluacionSupervisor.evaluacion_nombre || 'Evaluación 360°',
      
      // Promedios individuales
      promedio_autoevaluacion: parseFloat(promedioAutoevaluacion.toFixed(2)),
      promedio_supervisor: parseFloat(promedioSupervisor.toFixed(2)),
      
      // Ponderado final
      ponderado_final: parseFloat(ponderadoFinal.toFixed(2)),
      
      // Configuración de ponderación usada
      ponderacion_config: PONDERACION_CONFIG,
      
      // Referencias a evaluaciones originales
      autoevaluacion_id: autoevaluacion.id,
      evaluacion_supervisor_id: evaluacionSupervisor.id,
      
      // Información del evaluador (supervisor)
      evaluador_cedula: evaluacionSupervisor.evaluador_cedula,
      evaluador_nombre: evaluacionSupervisor.evaluador_nombre,
      
      // Fechas
      fecha_autoevaluacion: autoevaluacion.fecha_finalizacion,
      fecha_evaluacion_supervisor: evaluacionSupervisor.fecha_finalizacion,
      fecha_procesamiento: new Date().toISOString(),
      
      // Estado
      estado: 'procesada',
      version: '1.0'
    };
    
    // 7. Guardar o actualizar el documento
    if (!existeSnapshot.empty) {
      // Actualizar documento existente
      const docId = existeSnapshot.docs[0].id;
      await updateDoc(doc(db, 'eval180_terminada', docId), documentoEval180);
      console.log(`✅ Evaluación 180° actualizada para empleado: ${empleadoCedula} (ID: ${docId})`);
    } else {
      // Crear nuevo documento
      const docRef = await addDoc(collection(db, 'eval180_terminada'), documentoEval180);
      console.log(`✅ Nueva evaluación 180° creada para empleado: ${empleadoCedula} (ID: ${docRef.id})`);
    }
    
    return true;
    
  } catch (error) {
    console.error(`❌ Error procesando evaluación 180° para empleado ${empleadoCedula}:`, error);
    return false;
  }
};

const FormularioEvaluacionSubordinado = ({ evaluacion, onVolver }) => {
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [empleadoEvaluado, setEmpleadoEvaluado] = useState(null);
  const [empleadoEvaluador, setEmpleadoEvaluador] = useState(null);
  const [empleadoEvaluadoCompleto, setEmpleadoEvaluadoCompleto] = useState(null); // Información completa del empleado evaluado
  const [empleadoEvaluadorCompleto, setEmpleadoEvaluadorCompleto] = useState(null); // Información completa del evaluador
  const [plantilla, setPlantilla] = useState(null);
  const [preguntasPorCompetencia, setPreguntasPorCompetencia] = useState({});
  const [totalPreguntas, setTotalPreguntas] = useState(0);
  const [preguntasRespondidas, setPreguntasRespondidas] = useState(0);
  const [progreso, setProgreso] = useState(0);

  // Función helper para generar nombre completo con debugging
  const obtenerNombreCompleto = (empleadoCompleto, empleadoBasico, contexto = "") => {
    console.log(`🔍 DEBUG ${contexto} - obtenerNombreCompleto:`);
    console.log("   - empleadoCompleto:", empleadoCompleto);
    console.log("   - empleadoBasico:", empleadoBasico);
    
    if (empleadoCompleto) {
      const nombres = empleadoCompleto.nombres || empleadoCompleto.nombre || '';
      const apellidos = empleadoCompleto.apellidos || empleadoCompleto.apellido || '';
      const nombreCompleto = `${nombres} ${apellidos}`.trim();
      
      console.log(`   - nombres: "${nombres}"`);
      console.log(`   - apellidos: "${apellidos}"`);
      console.log(`   - nombreCompleto: "${nombreCompleto}"`);
      
      return nombreCompleto || empleadoBasico?.nombre || 'No especificado';
    } else {
      const fallback = empleadoBasico?.nombre || 'No especificado';
      console.log(`   - usando fallback: "${fallback}"`);
      return fallback;
    }
  };

  // Función para adaptar la estructura de la plantilla si es necesario
  const adaptarEstructuraPlantilla = async (plantillaData) => {
    console.log(`🔄 Adaptando plantilla para subordinado: ${plantillaData.id}`);
    if (plantillaData.preguntas && Array.isArray(plantillaData.preguntas)) {
      console.log('✅ Plantilla ya tiene formato con preguntas directas.');
      return { 'Competencias Generales': plantillaData.preguntas };
    }

    if (plantillaData.competenciasDetalle && plantillaData.competenciasSeleccionadas) {
      console.log('🔍 Adaptando desde formato nuevo (competenciasDetalle) con filtrado dinámico para subordinado.');
      const preguntasAgrupadas = {};
      const competenciasInfo = {};

      const uniqueCompetenciaIds = [...new Set(plantillaData.competenciasSeleccionadas)];

      if (uniqueCompetenciaIds.length > 0) {
        // Dividir en lotes para evitar el límite de Firebase de 30 elementos en consultas 'IN'
        const BATCH_SIZE = 10;
        const competenciasBatches = [];
        
        for (let i = 0; i < uniqueCompetenciaIds.length; i += BATCH_SIZE) {
          competenciasBatches.push(uniqueCompetenciaIds.slice(i, i + BATCH_SIZE));
        }
        
        console.log(`📦 Dividiendo ${uniqueCompetenciaIds.length} competencias en ${competenciasBatches.length} lotes para subordinado`);
        
        // Ejecutar consultas por lotes
        for (const batch of competenciasBatches) {
          const q = query(collection(db, "competencias"), where("__name__", "in", batch));
          const competenciasSnapshot = await getDocs(q);
          competenciasSnapshot.forEach(doc => {
            competenciasInfo[doc.id] = doc.data();
          });
        }
        
        console.log(`✅ Cargadas ${Object.keys(competenciasInfo).length} competencias en total para subordinado`);
      }

      uniqueCompetenciaIds.forEach((competenciaId) => {
        const competenciaInfo = competenciasInfo[competenciaId];
        const competenciaDetalle = plantillaData.competenciasDetalle[competenciaId];

        if (competenciaInfo && competenciaDetalle && competenciaDetalle.preguntas) {
          const nombreCompetencia = competenciaInfo.nombre || 'Competencia sin nombre';
          const tipoCompetencia = competenciaInfo.tipoCompetencia;
          
          let incluirCompetencia = false;

          // Aplicar reglas de filtrado dinámico para evaluación de subordinado CORREGIDAS
          if (tipoCompetencia === 'Comportamental' || tipoCompetencia === 'comportamental') {
            // Las competencias comportamentales se muestran a todos
            incluirCompetencia = true;
            console.log(`✅ Incluyendo competencia Comportamental para subordinado: ${nombreCompetencia}`);
          } else if (tipoCompetencia === 'Liderazgo' || tipoCompetencia === 'liderazgo') {
            // CORREGIDO: Incluir liderazgo solo si el evaluado es supervisor Y evaluado (ambas condiciones)
            if (empleadoEvaluado && empleadoEvaluado.esSupervisor && empleadoEvaluado.esEvaluado) {
              incluirCompetencia = true;
              console.log(`✅ Incluyendo competencia de Liderazgo para subordinado (el evaluado es supervisor Y evaluado): ${nombreCompetencia}`);
            } else {
              console.log(`❌ Omitiendo competencia de Liderazgo para subordinado (supervisor: ${empleadoEvaluado?.esSupervisor}, evaluado: ${empleadoEvaluado?.esEvaluado} - necesita ambos): ${nombreCompetencia}`);
            }
          } else if (tipoCompetencia === 'Funcional' || tipoCompetencia === 'funcional') {
            // CORREGIDO: Múltiples métodos de coincidencia para competencias funcionales (igual que en autoevaluaciones)
            const cargoEvaluado = empleadoEvaluado?.cargo || '';
            const cargoCompetencia = competenciaInfo.cargoEspecifico || '';
            const posicionCompetencia = competenciaInfo.posicionEspecifica || '';
            const nombreCompetencia = competenciaInfo.nombre || '';
            
            console.log(`🔍 Comparando cargos para competencia funcional de subordinado "${nombreCompetencia}":`);
            console.log(`   - Cargo del empleado evaluado: "${cargoEvaluado}"`);
            console.log(`   - Cargo de la competencia: "${cargoCompetencia}"`);
            console.log(`   - Posición de la competencia: "${posicionCompetencia}"`);
            console.log(`   - Nombre de la competencia: "${nombreCompetencia}"`);
            
            // Método 1: Coincidencia exacta con cargoEspecifico
            let coincidenciaExacta = cargoEvaluado && cargoCompetencia && 
                cargoEvaluado.toLowerCase().trim() === cargoCompetencia.toLowerCase().trim();
            
            // Método 2: Coincidencia con posicionEspecifica
            let coincidenciaPosicion = false;
            if (!coincidenciaExacta && cargoEvaluado && posicionCompetencia) {
                coincidenciaPosicion = cargoEvaluado.toLowerCase().trim() === posicionCompetencia.toLowerCase().trim();
            }
            
            // Método 3: Coincidencia por nombre de la competencia (fallback para datos corruptos)
            let coincidenciaPorNombre = false;
            if (!coincidenciaExacta && !coincidenciaPosicion && cargoEvaluado && nombreCompetencia) {
                coincidenciaPorNombre = cargoEvaluado.toLowerCase().trim() === nombreCompetencia.toLowerCase().trim();
            }
            
            console.log(`   - Comparación cargoEspecifico: "${cargoEvaluado.toLowerCase().trim()}" === "${cargoCompetencia.toLowerCase().trim()}" = ${coincidenciaExacta}`);
            console.log(`   - Comparación posicionEspecifica: "${cargoEvaluado.toLowerCase().trim()}" === "${posicionCompetencia.toLowerCase().trim()}" = ${coincidenciaPosicion}`);
            console.log(`   - Comparación nombreCompetencia: "${cargoEvaluado.toLowerCase().trim()}" === "${nombreCompetencia.toLowerCase().trim()}" = ${coincidenciaPorNombre}`);
            
            if (coincidenciaExacta || coincidenciaPosicion || coincidenciaPorNombre) {
              incluirCompetencia = true;
              let tipoCoincidencia = coincidenciaExacta ? 'cargoEspecifico' : 
                                   coincidenciaPosicion ? 'posicionEspecifica' : 'nombreCompetencia';
              console.log(`✅ Incluyendo competencia Funcional para subordinado (cargo coincide por ${tipoCoincidencia}): ${nombreCompetencia}`);
            } else {
              console.log(`❌ Omitiendo competencia Funcional para subordinado - Razones:`);
              if (!cargoEvaluado) console.log(`   - Cargo del empleado evaluado vacío o nulo`);
              if (!cargoCompetencia && !posicionCompetencia && !nombreCompetencia) {
                  console.log(`   - Ningún campo de cargo disponible en la competencia`);
              }
              if (cargoEvaluado && (cargoCompetencia || posicionCompetencia || nombreCompetencia)) {
                  console.log(`   - Ninguna coincidencia encontrada:`);
                  console.log(`     * "${cargoEvaluado}" ≠ "${cargoCompetencia}" (cargoEspecifico)`);
                  console.log(`     * "${cargoEvaluado}" ≠ "${posicionCompetencia}" (posicionEspecifica)`);
                  console.log(`     * "${cargoEvaluado}" ≠ "${nombreCompetencia}" (nombreCompetencia)`);
              }
            }
          } else {
            // Si no tiene tipo específico, incluirla por defecto (compatibilidad hacia atrás)
            incluirCompetencia = true;
            console.log(`⚠️ Incluyendo competencia sin tipo específico para subordinado: ${nombreCompetencia} (tipo: ${tipoCompetencia})`);
          }

          if (incluirCompetencia) {
            // Agrupar por TIPO de competencia en lugar de por nombre individual
            let grupoCompetencia = '';
            if (tipoCompetencia === 'Comportamental' || tipoCompetencia === 'comportamental') {
              grupoCompetencia = '🎯 Competencias Comportamentales';
            } else if (tipoCompetencia === 'Liderazgo' || tipoCompetencia === 'liderazgo') {
              grupoCompetencia = '👥 Competencias de Liderazgo (Habilidades Blandas)';
            } else if (tipoCompetencia === 'Funcional' || tipoCompetencia === 'funcional') {
              grupoCompetencia = '🛠️ Competencias Funcionales';
            } else {
              grupoCompetencia = '📋 Otras Competencias';
            }
            
            if (!preguntasAgrupadas[grupoCompetencia]) {
              preguntasAgrupadas[grupoCompetencia] = [];
            }
            
            // CORREGIDO: Eliminar preguntas duplicadas usando un Set basado en el texto
            const preguntasUnicas = [];
            const textosVistos = new Set();
            
            competenciaDetalle.preguntas.forEach((pregunta, pregIndex) => {
              const textoPregunta = pregunta.tercera_persona || pregunta.primera_persona || pregunta.texto || '';
              if (!textosVistos.has(textoPregunta.trim().toLowerCase()) && textoPregunta.trim()) {
                textosVistos.add(textoPregunta.trim().toLowerCase());
                preguntasUnicas.push({
                  id: `${competenciaId}_${pregIndex}`,
                  texto: textoPregunta,
                  tipo: pregunta.tipo || "escala",
                  descripcion: pregunta.descripcion || '',
                  nombreCompetencia: nombreCompetencia, // Agregamos el nombre de la competencia individual
                  competenciaId: competenciaId
                });
              }
            });

            console.log(`🔍 Competencia "${nombreCompetencia}" → Grupo "${grupoCompetencia}": ${competenciaDetalle.preguntas.length} preguntas originales → ${preguntasUnicas.length} preguntas únicas`);
            preguntasAgrupadas[grupoCompetencia].push(...preguntasUnicas);
          }
        } else {
          console.warn(`⚠️ No se encontró la competencia con ID: ${competenciaId}`);
        }
      });
      
      console.log(`✅ Competencias filtradas dinámicamente para subordinado: ${Object.keys(preguntasAgrupadas).length}`);
      
      // FALLBACK: Si no se incluyó ninguna competencia, incluir al menos las disponibles
      if (Object.keys(preguntasAgrupadas).length === 0 && uniqueCompetenciaIds.length > 0) {
        console.log('🔄 No se encontraron competencias después del filtrado para subordinado, aplicando fallback...');
        const grupoFallback = '📋 Competencias Generales (Fallback)';
        
        uniqueCompetenciaIds.forEach((competenciaId) => {
          const competenciaInfo = competenciasInfo[competenciaId];
          const competenciaDetalle = plantillaData.competenciasDetalle[competenciaId];
          
          if (competenciaInfo && competenciaDetalle && competenciaDetalle.preguntas) {
            const nombreCompetencia = competenciaInfo.nombre || 'Competencia sin nombre';
            console.log(`🔄 Aplicando fallback para competencia de subordinado: ${nombreCompetencia}`);
            
            if (!preguntasAgrupadas[grupoFallback]) {
              preguntasAgrupadas[grupoFallback] = [];
            }
            
            const preguntasUnicas = [];
            const textosVistos = new Set();
            
            competenciaDetalle.preguntas.forEach((pregunta, pregIndex) => {
              const textoPregunta = pregunta.tercera_persona || pregunta.primera_persona || pregunta.texto || '';
              if (!textosVistos.has(textoPregunta.trim().toLowerCase()) && textoPregunta.trim()) {
                textosVistos.add(textoPregunta.trim().toLowerCase());
                preguntasUnicas.push({
                  id: `${competenciaId}_${pregIndex}`,
                  texto: textoPregunta,
                  tipo: pregunta.tipo || "escala",
                  descripcion: pregunta.descripcion || '',
                });
              }
            });
            
            preguntasAgrupadas[grupoFallback].push(...preguntasUnicas);
            console.log(`✅ Fallback aplicado para subordinado: ${nombreCompetencia} (${preguntasUnicas.length} preguntas)`);
          }
        });
      }
      
      return preguntasAgrupadas;
    }

    console.log('❌ Formato de plantilla no reconocido.');
    return {};
  };

  useEffect(() => {
    const cargarDatos = async () => {
      if (!evaluacion) return;
      setLoading(true);
      try {
        console.log('🔍 Cargando datos para evaluación de subordinado:', evaluacion);
        
        // Cargar datos del evaluador (quien está logueado)
        const empleadoEvaluadorSession = localStorage.getItem('empleadoSession');
        if (empleadoEvaluadorSession) {
          const evaluadorData = JSON.parse(empleadoEvaluadorSession);
          setEmpleadoEvaluador(evaluadorData);
          console.log('👤 Datos del evaluador cargados:', evaluadorData);
        }
        
        // Cargar información completa del empleado evaluado
        const qEmpleadoEvaluado = query(collection(db, "empleados"), where("cedula", "==", evaluacion.empleadoCedula));
        const empleadoEvaluadoSnapshot = await getDocs(qEmpleadoEvaluado);
        
        let empleadoEvaluadoCompleto = {
          nombre: evaluacion.empleadoNombre,
          cedula: evaluacion.empleadoCedula,
          cargo: evaluacion.empleadoCargo || 'No especificado',
          esSupervisor: false,
          esEvaluado: false
        };

        if (!empleadoEvaluadoSnapshot.empty) {
          const empleadoDoc = empleadoEvaluadoSnapshot.docs[0].data();
          empleadoEvaluadoCompleto.cargo = empleadoDoc.cargo || empleadoEvaluadoCompleto.cargo;
          empleadoEvaluadoCompleto.esEvaluado = !!empleadoDoc.supervisor_cedula;
          
          console.log(`🔍 Verificando estado del empleado evaluado: ${empleadoEvaluadoCompleto.nombre} (${empleadoEvaluadoCompleto.cedula})`);
          console.log(`👤 Cargo del empleado evaluado: "${empleadoEvaluadoCompleto.cargo}"`);
          console.log(`👤 Supervisor cedula: "${empleadoDoc.supervisor_cedula}"`);
          
          // Verificar si el empleado evaluado es supervisor de alguien
          const qEsSupervisor = query(collection(db, "empleados"), where("supervisor_cedula", "==", evaluacion.empleadoCedula));
          const esSupervisorSnapshot = await getDocs(qEsSupervisor);
          empleadoEvaluadoCompleto.esSupervisor = !esSupervisorSnapshot.empty;
          
          console.log(`👤 ¿El empleado evaluado es supervisor? ${empleadoEvaluadoCompleto.esSupervisor} (supervisa a ${esSupervisorSnapshot.size} empleados)`);
          console.log(`👤 ¿El empleado evaluado es evaluado? ${empleadoEvaluadoCompleto.esEvaluado} (tiene supervisor asignado)`);
          console.log(`🎯 Resumen del estado: supervisor=${empleadoEvaluadoCompleto.esSupervisor}, evaluado=${empleadoEvaluadoCompleto.esEvaluado} → Liderazgo=${empleadoEvaluadoCompleto.esSupervisor && empleadoEvaluadoCompleto.esEvaluado}`);
        }
        
        setEmpleadoEvaluado(empleadoEvaluadoCompleto);
        
        if (evaluacion.plantillaId) {
            console.log(`📄 Cargando plantilla ID: ${evaluacion.plantillaId}`);
            const plantillaDoc = await getDoc(doc(db, "plantillas_evaluacion", evaluacion.plantillaId));
            if (plantillaDoc.exists()) {
              const plantillaData = { id: plantillaDoc.id, ...plantillaDoc.data() };
              setPlantilla(plantillaData);
              // Esperar a que employeeData esté disponible antes de adaptar
              const preguntasAdaptadas = await adaptarEstructuraPlantilla(plantillaData);
              setPreguntasPorCompetencia(preguntasAdaptadas);
              
              // Calcular el total de preguntas de escala para el progreso
              const totalPreguntasEscala = Object.values(preguntasAdaptadas).flat().filter(p => p.tipo === 'escala').length;
              setTotalPreguntas(totalPreguntasEscala);
            } else {
              console.error(`❌ No se encontró la plantilla con ID: ${evaluacion.plantillaId}`);
              setPreguntasPorCompetencia({});
            }
        } else {
            console.error('❌ La evaluación no tiene un plantillaId.');
            setPreguntasPorCompetencia({});
        }

        // Cargar las respuestas guardadas como borrador
        if (evaluacion.respuestas_supervisor) {
          setRespuestas(evaluacion.respuestas_supervisor);
        } else {
          setRespuestas({});
        }
        
      } catch (error) {
        console.error('❌ Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [evaluacion]);

  // Efecto adicional para re-adaptar la plantilla cuando el empleado evaluado esté completamente cargado
  useEffect(() => {
    const readaptarPlantilla = async () => {
      if (plantilla && empleadoEvaluado && empleadoEvaluado.esSupervisor !== undefined && empleadoEvaluado.esEvaluado !== undefined) {
        console.log('🔄 Re-adaptando plantilla con información completa del empleado evaluado');
        const preguntasAdaptadas = await adaptarEstructuraPlantilla(plantilla);
        setPreguntasPorCompetencia(preguntasAdaptadas);
        
        // Recalcular el total de preguntas de escala
        const totalPreguntasEscala = Object.values(preguntasAdaptadas).flat().filter(p => p.tipo === 'escala').length;
        setTotalPreguntas(totalPreguntasEscala);
      }
    };

    readaptarPlantilla();
  }, [empleadoEvaluado, plantilla]);

  // Efecto para calcular el progreso en tiempo real
  useEffect(() => {
    if (totalPreguntas > 0) {
      const respondidas = Object.keys(respuestas).filter(k => 
        respuestas[k]?.calificacion !== undefined && respuestas[k]?.calificacion !== null
      ).length;
      setPreguntasRespondidas(respondidas);
      const porcentaje = Math.round((respondidas / totalPreguntas) * 100);
      setProgreso(porcentaje);
    } else {
      setPreguntasRespondidas(0);
      setProgreso(0);
    }
  }, [respuestas, totalPreguntas]);

  // Cargar información completa de los empleados desde la colección empleados
  useEffect(() => {
    const cargarEmpleadosCompletos = async () => {
      try {
        // Cargar información completa del empleado evaluado
        if (empleadoEvaluado && empleadoEvaluado.cedula) {
          console.log(`🔍 Cargando información completa del empleado evaluado: ${empleadoEvaluado.cedula}`);
          const qEvaluado = query(collection(db, "empleados"), where("cedula", "==", empleadoEvaluado.cedula));
          const evaluadoSnapshot = await getDocs(qEvaluado);
          
          if (!evaluadoSnapshot.empty) {
            const evaluadoData = evaluadoSnapshot.docs[0].data();
            console.log("👤 Información completa del empleado evaluado cargada:", evaluadoData);
            console.log("🔍 DEBUG - Campos del empleado evaluado:");
            console.log("   - nombres:", evaluadoData.nombres);
            console.log("   - nombre:", evaluadoData.nombre);
            console.log("   - apellidos:", evaluadoData.apellidos);
            console.log("   - apellido:", evaluadoData.apellido);
            console.log("   - Todos los campos:", Object.keys(evaluadoData));
            setEmpleadoEvaluadoCompleto(evaluadoData);
          } else {
            console.warn("⚠️ No se encontró información completa del empleado evaluado en la colección empleados");
            setEmpleadoEvaluadoCompleto(null);
          }
        }

        // Cargar información completa del empleado evaluador
        if (empleadoEvaluador && empleadoEvaluador.cedula) {
          console.log(`🔍 Cargando información completa del empleado evaluador: ${empleadoEvaluador.cedula}`);
          const qEvaluador = query(collection(db, "empleados"), where("cedula", "==", empleadoEvaluador.cedula));
          const evaluadorSnapshot = await getDocs(qEvaluador);
          
          if (!evaluadorSnapshot.empty) {
            const evaluadorData = evaluadorSnapshot.docs[0].data();
            console.log("👤 Información completa del empleado evaluador cargada:", evaluadorData);
            console.log("🔍 DEBUG - Campos del empleado evaluador:");
            console.log("   - nombres:", evaluadorData.nombres);
            console.log("   - nombre:", evaluadorData.nombre);
            console.log("   - apellidos:", evaluadorData.apellidos);
            console.log("   - apellido:", evaluadorData.apellido);
            console.log("   - Todos los campos:", Object.keys(evaluadorData));
            setEmpleadoEvaluadorCompleto(evaluadorData);
          } else {
            console.warn("⚠️ No se encontró información completa del empleado evaluador en la colección empleados");
            setEmpleadoEvaluadorCompleto(null);
          }
        }
      } catch (error) {
        console.error("❌ Error cargando información completa de los empleados:", error);
        setEmpleadoEvaluadoCompleto(null);
        setEmpleadoEvaluadorCompleto(null);
      }
    };

    cargarEmpleadosCompletos();
  }, [empleadoEvaluado, empleadoEvaluador]);

  const handleRespuestaChange = (preguntaId, tipo, valor) => {
    setRespuestas(prev => {
      const newState = {
        ...prev,
        [preguntaId]: {
          ...prev[preguntaId],
          [tipo]: valor,
        },
      };
      return newState;
    });
  };

  const handleGuardar = async (finalizar = false) => {
    setGuardando(true);
    try {
      const evaluador = empleadoEvaluador || {};

      // 1. Validar si se puede finalizar
      if (finalizar) {
        const totalPreguntasEscala = Object.values(preguntasPorCompetencia).flat().filter(p => p.tipo === 'escala').length;
        const respondidas = Object.keys(respuestas).filter(k => respuestas[k]?.calificacion).length;
        
        if (respondidas < totalPreguntasEscala) {
          alert(`Debe responder todas las ${totalPreguntasEscala} preguntas obligatorias para poder finalizar la evaluación. Faltan ${totalPreguntasEscala - respondidas}.`);
          setGuardando(false);
          return;
        }
      }

      // 2. Actualizar la evaluación asignada para reflejar el estado
      const docRefAsignada = doc(db, 'evaluaciones_asignadas', evaluacion.id);
      const updateData = {
        // Guardamos un borrador de las respuestas del supervisor en la evaluación asignada
        respuestas_supervisor: respuestas,
        estado_supervisor: finalizar ? 'completada' : 'en_progreso',
        fecha_ultima_actualizacion: new Date().toISOString(),
      };

      // IMPORTANTE: También actualizar el estado general para que aparezca correctamente en el dashboard
      if (finalizar) {
        updateData.fecha_finalizacion_supervisor = new Date().toISOString();
        updateData.estado = 'completada'; // Marcar la evaluación completa en el estado general
      }

      await updateDoc(docRefAsignada, updateData);
      console.log(`✅ Documento en evaluaciones_asignadas actualizado. Estado: ${updateData.estado_supervisor}`);

      // 3. Si se finaliza, crear el registro definitivo en 'evaluaciones_realizadas'
      if (finalizar) {
        console.log('📝 Creando registro en evaluaciones_realizadas...');
        await addDoc(collection(db, 'evaluaciones_realizadas'), {
          evaluacion_asignada_id: evaluacion.id,
          plantilla_id: evaluacion.plantillaId,
          empleado_cedula: empleadoEvaluado.cedula,
          empleado_nombre: empleadoEvaluadoCompleto 
            ? `${empleadoEvaluadoCompleto.nombres || empleadoEvaluadoCompleto.nombre || ''} ${empleadoEvaluadoCompleto.apellidos || ''}`.trim() || empleadoEvaluado.nombre
            : empleadoEvaluado.nombre,
          evaluador_cedula: evaluador.cedula,
          evaluador_nombre: empleadoEvaluadorCompleto 
            ? `${empleadoEvaluadorCompleto.nombres || empleadoEvaluadorCompleto.nombre || ''} ${empleadoEvaluadorCompleto.apellidos || ''}`.trim() || evaluador.nombre
            : evaluador.nombre,
          respuestas: respuestas,
          fecha_finalizacion: updateData.fecha_finalizacion_supervisor,
        });
        console.log('✅ Registro en evaluaciones_realizadas creado exitosamente.');

        // 4. NUEVO: Procesar automáticamente la evaluación 180° si están completas ambas evaluaciones
        console.log('🔄 Verificando si se puede procesar la evaluación 180° automáticamente...');
        try {
          const procesado = await procesarEvaluacion180Automatica(empleadoEvaluado.cedula);
          if (procesado) {
            console.log('🎉 ¡Evaluación 180° procesada automáticamente!');
          } else {
            console.log('📋 Evaluación 180° no procesada: faltan evaluaciones por completar.');
          }
        } catch (error) {
          console.error('❌ Error en procesamiento automático de evaluación 180°:', error);
          // No interrumpir el flujo principal si falla el procesamiento automático
        }
      }

      alert(`Evaluación ${finalizar ? 'finalizada y enviada' : 'guardada como borrador'} exitosamente.`);
      
      if (finalizar) {
        // Usamos un pequeño retraso para asegurar que el usuario vea el mensaje
        // y luego se ejecute la función para volver.
        setTimeout(() => {
          onVolver();
        }, 500);
      }
    } catch (error) {
      console.error('❌ Error guardando evaluación:', error);
      alert('Error al guardar la evaluación. Por favor, intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Cargando formulario de evaluación...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'white', borderRadius: '8px', padding: '2rem' }}>
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={onVolver}
            style={{
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '1rem'
            }}
          >
            ← Volver al Dashboard
          </button>
          
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '1rem' }}>
            Evaluación de Subordinado
          </h1>

          {/* Encabezado de Bienvenida para Evaluación de Subordinado */}
          <div style={{ 
            backgroundColor: '#f0fdf4', 
            padding: '2rem', 
            borderRadius: '12px', 
            border: '1px solid #16a34a',
            marginBottom: '2rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#14532d', 
              margin: '0 0 1rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Evaluación 360° - Tu perspectiva importa 🎯
            </h2>
            
            <p style={{ 
              color: '#14532d', 
              margin: '0 0 1.5rem 0', 
              lineHeight: '1.6',
              fontSize: '1rem'
            }}>
              Te invitamos a participar en la evaluación de <strong>{obtenerNombreCompleto(empleadoEvaluadoCompleto, empleadoEvaluado, "INTRO_EVALUADO")}</strong> como parte de nuestro proceso de evaluación 360°. 
              Tu perspectiva como supervisor/colega es valiosa para proporcionar una retroalimentación constructiva que contribuya al desarrollo profesional.
            </p>

            {/* Información del Empleado Evaluado */}
            <div style={{ 
              backgroundColor: '#dcfce7', 
              padding: '1rem', 
              borderRadius: '8px',
              border: '1px solid #22c55e',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: '#14532d', 
                margin: '0 0 0.5rem 0'
              }}>
                👤 Empleado a Evaluar
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                <div>
                  <strong style={{ color: '#14532d' }}>Nombre:</strong> {obtenerNombreCompleto(empleadoEvaluadoCompleto, empleadoEvaluado, "INFO_EVALUADO")}
                </div>
                <div>
                  <strong style={{ color: '#14532d' }}>Cédula:</strong> {empleadoEvaluado?.cedula || evaluacion?.empleadoCedula || 'No especificada'}
                </div>
                <div>
                  <strong style={{ color: '#14532d' }}>Cargo:</strong> {empleadoEvaluadoCompleto?.cargo || empleadoEvaluado?.cargo || 'No especificado'}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                fontSize: '1.1rem', 
                fontWeight: '600', 
                color: '#14532d', 
                margin: '0 0 0.75rem 0'
              }}>
                ¿Por qué es importante tu evaluación?
              </h3>
              <ul style={{ 
                color: '#14532d', 
                margin: '0 0 0 1.5rem', 
                lineHeight: '1.6',
                paddingLeft: '0.5rem'
              }}>
                <li style={{ marginBottom: '0.25rem' }}>Brinda una perspectiva externa objetiva sobre el desempeño</li>
                <li style={{ marginBottom: '0.25rem' }}>Ayuda a identificar fortalezas y áreas de mejora desde tu punto de vista</li>
                <li style={{ marginBottom: '0.25rem' }}>Contribuye al desarrollo profesional integral</li>
                <li>Fortalece la comunicación y colaboración en el equipo</li>
              </ul>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                fontSize: '1.1rem', 
                fontWeight: '600', 
                color: '#14532d', 
                margin: '0 0 0.75rem 0'
              }}>
                Sugerencias para una evaluación efectiva:
              </h3>
              <ul style={{ 
                color: '#14532d', 
                margin: '0 0 0 1.5rem', 
                lineHeight: '1.6',
                paddingLeft: '0.5rem'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Sé constructivo y específico</strong> - Proporciona ejemplos concretos cuando sea posible.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Enfócate en comportamientos observables</strong> - Basa tus comentarios en acciones y resultados específicos.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Mantén un enfoque profesional</strong> - La evaluación debe centrarse en el desempeño laboral.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Balancea fortalezas y oportunidades</strong> - Reconoce los logros y sugiere áreas de crecimiento.
                </li>
              </ul>
            </div>

            <div style={{ 
              backgroundColor: '#dcfce7', 
              padding: '1rem', 
              borderRadius: '8px',
              border: '1px solid #22c55e'
            }}>
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: '#14532d', 
                margin: '0 0 0.5rem 0'
              }}>
                Recuerda:
              </h3>
              <p style={{ 
                color: '#14532d', 
                margin: '0 0 0.75rem 0', 
                lineHeight: '1.6',
                fontSize: '0.95rem'
              }}>
                Tu retroalimentación honesta y constructiva es fundamental para el crecimiento profesional. 
                Este proceso de evaluación 360° busca crear un ambiente de desarrollo continuo y colaboración.
              </p>
              <p style={{ 
                color: '#14532d', 
                margin: '0', 
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                ¡Gracias por tu tiempo y por contribuir al desarrollo de tu colega!
              </p>
            </div>
          </div>
          
          <div style={{ backgroundColor: '#f1f5f9', padding: '1rem', borderRadius: '6px', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#334155', marginBottom: '0.5rem' }}>
              {evaluacion.evaluacionNombre}
            </h2>
            <p style={{ color: '#64748b', margin: 0 }}>
              <strong>Empleado a evaluar:</strong> {empleadoEvaluado?.nombre} ({empleadoEvaluado?.cedula})
            </p>
            <p style={{ color: '#64748b', margin: '0.25rem 0 0 0' }}>
              <strong>Cargo:</strong> {empleadoEvaluado?.cargo}
            </p>
          </div>

          {/* Barra de Progreso */}
          {totalPreguntas > 0 && (
            <div style={{ 
              backgroundColor: '#f8fafc', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb',
              marginBottom: '1.5rem'
            }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <h3 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  color: '#374151', 
                  margin: '0 0 0.5rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📊 Progreso de la Evaluación
                </h3>
                <p style={{ 
                  fontSize: '0.9rem', 
                  color: '#6b7280', 
                  margin: 0 
                }}>
                  {preguntasRespondidas} de {totalPreguntas} preguntas respondidas ({progreso}%)
                </p>
              </div>
              
              <div style={{ 
                backgroundColor: '#e5e7eb', 
                borderRadius: '9999px', 
                height: '0.75rem', 
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div
                  style={{
                    backgroundColor: progreso === 100 ? '#10b981' : '#3b82f6',
                    width: `${progreso}%`,
                    height: '100%',
                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease-in-out',
                    borderRadius: '9999px'
                  }}
                />
              </div>
              
              {progreso === 100 && (
                <div style={{ 
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  backgroundColor: '#dcfce7',
                  borderRadius: '6px',
                  border: '1px solid #16a34a'
                }}>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#166534', 
                    margin: 0,
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    ✅ ¡Todas las preguntas han sido respondidas! Puedes finalizar y enviar la evaluación.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {Object.keys(preguntasPorCompetencia).length > 0 ? (
          <form onSubmit={(e) => e.preventDefault()}>
            {Object.keys(preguntasPorCompetencia).map((nombreGrupo, compIndex) => {
              // Agrupar preguntas por competencia individual dentro del grupo
              const preguntasPorCompetenciaIndividual = {};
              
              preguntasPorCompetencia[nombreGrupo].forEach(pregunta => {
                const nombreCompetencia = pregunta.nombreCompetencia || 'Competencia sin nombre';
                if (!preguntasPorCompetenciaIndividual[nombreCompetencia]) {
                  preguntasPorCompetenciaIndividual[nombreCompetencia] = [];
                }
                preguntasPorCompetenciaIndividual[nombreCompetencia].push(pregunta);
              });

              return (
                <div key={compIndex} style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '1.5rem', borderBottom: '2px solid #dbeafe', paddingBottom: '0.5rem' }}>
                    {nombreGrupo}
                  </h2>
                  
                  {/* Renderizar cada competencia individual dentro del grupo */}
                  {Object.keys(preguntasPorCompetenciaIndividual).map((nombreCompetencia, competenciaIndex) => (
                    <div key={competenciaIndex} style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#059669', marginBottom: '1rem', borderBottom: '1px solid #d1fae5', paddingBottom: '0.5rem' }}>
                        📋 {nombreCompetencia}
                      </h3>
                      
                      {preguntasPorCompetenciaIndividual[nombreCompetencia].map((pregunta, pregIndex) => (
                        <div key={pregunta.id || pregIndex} style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                            {pregIndex + 1}. {pregunta.texto}
                          </h4>
                          
                          {pregunta.descripcion && (
                            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                              {pregunta.descripcion}
                            </p>
                          )}

                          {pregunta.tipo === 'escala' ? (
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Calificación:</span>
                              {[1, 2, 3, 4, 5].map(valor => (
                                <label key={valor} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                  <input
                                    type="radio"
                                    name={`pregunta_${pregunta.id}`}
                                    value={valor}
                                    checked={respuestas[pregunta.id]?.calificacion == valor}
                                    onChange={(e) => handleRespuestaChange(pregunta.id, 'calificacion', parseInt(e.target.value))}
                                    style={{ marginRight: '0.25rem' }}
                                  />
                                  <span style={{ fontSize: '0.875rem' }}>{valor}</span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <textarea
                              value={respuestas[pregunta.id]?.comentario || ''}
                              onChange={(e) => handleRespuestaChange(pregunta.id, 'comentario', e.target.value)}
                              placeholder="Escribe tu respuesta aquí..."
                              style={{
                                width: '100%',
                                minHeight: '100px',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                resize: 'vertical'
                              }}
                            />
                          )}
                        </div>
                      ))}
                      
                      {/* Comentarios adicionales para esta competencia específica */}
                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                          💬 Comentarios sobre {nombreCompetencia}
                        </h4>
                        <textarea
                          value={respuestas[`comentario_competencia_${nombreCompetencia}`]?.comentario || ''}
                          onChange={(e) => handleRespuestaChange(`comentario_competencia_${nombreCompetencia}`, 'comentario', e.target.value)}
                          placeholder="Añade comentarios específicos sobre esta competencia..."
                          style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Autorización para Tratamiento de Datos Personales */}
            <div style={{ 
              marginTop: '2rem', 
              padding: '1rem', 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px' 
            }}>
              <h4 style={{ 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '0.5rem' 
              }}>
                📋 Autorización para Tratamiento de Datos Personales – Evaluación de Desempeño
              </h4>
              <p style={{ 
                fontSize: '0.75rem', 
                color: '#6b7280', 
                lineHeight: '1.4',
                margin: '0'
              }}>
                Yo, <strong>{obtenerNombreCompleto(empleadoEvaluadorCompleto, empleadoEvaluador, "AUTORIZACION_EVALUADOR")}</strong>, identificado(a) con cédula No. <strong>{empleadoEvaluador?.cedula || '[número]'}</strong>, autorizo de manera libre, previa e informada a <strong>{evaluacion?.empresaNombre || '[nombre de la empresa]'}</strong> para tratar mis datos personales con el fin de aplicar y gestionar la evaluación de desempeño laboral de <strong>{obtenerNombreCompleto(empleadoEvaluadoCompleto, empleadoEvaluado, "AUTORIZACION_EVALUADO")}</strong>.
                <br /><br />
                Declaro que he sido informado(a) sobre la finalidad del proceso, la confidencialidad de la información, y mis derechos de conocer, actualizar, rectificar o suprimir mis datos conforme a la Ley 1581 de 2012.
              </p>
            </div>

            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => handleGuardar(false)}
                disabled={guardando}
                style={{ backgroundColor: '#6b7280', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem' }}
              >
                {guardando ? 'Guardando...' : 'Guardar Borrador'}
              </button>
              <button
                onClick={() => handleGuardar(true)}
                disabled={guardando}
                style={{ backgroundColor: '#1e40af', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold' }}
              >
                {guardando ? 'Finalizando...' : 'Finalizar y Enviar'}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No hay preguntas para mostrar. La plantilla podría estar vacía o no ser aplicable.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormularioEvaluacionSubordinado;